import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { useBlogPosts } from '../../../hooks/useBlogPosts';
import BlogPostForm from '../../../components/blog/BlogPostForm';
import { ArrowLeft } from 'lucide-react';

const BlogPostEditor = () => {
  const { id } = useParams(); // undefined for new posts
  const navigate = useNavigate();
  const { user: currentUser } = useAuth(); // Fix: get user as currentUser
  const { 
    categories, 
    fetchPost, 
    createPost, 
    updatePost, 
    loading, 
    error, 
    clearError 
  } = useBlogPosts();

  const [post, setPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingPost, setLoadingPost] = useState(!!id);

  const isEditing = !!id;

  // Load existing post if editing
  useEffect(() => {
    const loadPost = async () => {
      if (!id) return;
      
      try {
        setLoadingPost(true);
        const existingPost = await fetchPost(id);
        setPost(existingPost);
      } catch (err) {
        console.error('Error loading post:', err);
      } finally {
        setLoadingPost(false);
      }
    };

    loadPost();
  }, [id, fetchPost]);

  const handleSave = async (postData) => {
    if (!currentUser) {
      throw new Error('You must be logged in to save posts');
    }

    setSaving(true);
    clearError();

    try {
      const authorData = {
        uid: currentUser.uid,
        name: currentUser.name || currentUser.displayName,
        email: currentUser.email
      };

      let savedPost;
      if (isEditing) {
        savedPost = await updatePost(id, postData, authorData);
      } else {
        savedPost = await createPost(postData, authorData);
      }

      // Navigate back to dashboard or to edit mode for new posts
      if (isEditing) {
        navigate('/admin/blog');
      } else {
        navigate(`/admin/blog/posts/edit/${savedPost.id}`);
      }
    } catch (err) {
      console.error('Error saving post:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/blog');
  };

  if (loadingPost) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading post...</p>
        </div>
      </div>
    );
  }

  if (isEditing && !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Post Not Found</h2>
          <p className="text-gray-600 mb-6">
            The blog post you're looking for doesn't exist or you don't have permission to edit it.
          </p>
          <button
            onClick={() => navigate('/admin/blog')}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
          >
            Back to Blog Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-post-editor min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation */}
        <div className="mb-6">
          <button
            onClick={handleCancel}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Blog Dashboard
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error {isEditing ? 'loading' : 'creating'} post
                </h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Main Editor */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <BlogPostForm
            key={post?.id || 'new'}
            post={post}
            onSave={handleSave}
            onCancel={handleCancel}
            loading={saving}
            categories={categories}
          />
        </div>

        {/* Help Text */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">
            Writing Tips for Dental Hygienists
          </h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Focus on practical, actionable advice your fellow hygienists can use</li>
            <li>• Include relevant keywords naturally to improve search visibility</li>
            <li>• Use headings to structure your content for easy scanning</li>
            <li>• Add a compelling featured image to increase engagement</li>
            <li>• Write a clear, informative excerpt to entice readers</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BlogPostEditor;
